import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RoleDto } from 'src/app/core/services/user-api.service';
import { ProjectStore } from 'src/app/core/state/project.store';
import { ViewAccessStore } from 'src/app/core/state/viewAccess.store';
import { IconSizeDirective } from 'src/app/shared/directives/icon-size.directive';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { HideUserRoleApiService, RoleName } from 'src/app/shell/user-roles/hide-user-roles-api.service';
import { AuthService } from '../../core/services/auth.service';
import { UserService } from '../../core/services/user.service';
import { RolePrettyPrintPipe } from '../../shared/pipes/user-setup.pipe';

@Component({
  selector: 'app-user-roles',
  templateUrl: './user-roles.component.html',
  styleUrl: './user-roles.component.scss',
  imports: [
    IconSizeDirective,
    MatButtonModule,
    MatExpansionModule,
    MatIconModule,
    MatSlideToggleModule,
    RolePrettyPrintPipe,
  ],
  providers: [HideUserRoleApiService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserRolesComponent {
  readonly #auth = inject(AuthService);
  readonly #projectStore = inject(ProjectStore);
  readonly #userService = inject(UserService);
  readonly #hideUserRoleApiService = inject(HideUserRoleApiService);
  readonly #viewAccessStore = inject(ViewAccessStore);
  readonly #navigationService = inject(NavigationService);

  readonly #projectId = this.#projectStore.currentProjectId;
  readonly currentProjectName = computed(() => this.#projectStore.currentProject()?.name);
  readonly isTenantAdmin = toSignal(this.#auth.hasTenantAdminRole());

  readonly userRolesResource = rxResource({
    params: () => this.#projectId(),
    stream: ({ params: projectId }) => this.#userService.getCurrentUserProjectRoles({ projectId }),
  });
  readonly userRoles = computed(() => this.userRolesResource.value() || []);

  // Track expanded/collapsed state for role rows in-memory for the session
  readonly expanded = signal<Record<string, boolean>>({});

  toggleExpanded(roleName?: string) {
    if (!roleName) {
      return;
    }
    this.expanded.update(map => ({ ...map, [roleName]: !map[roleName] }));
  }

  getChildValues(role: RoleDto): string[] {
    const disciplineValues = this.#normalizeAndSort(role.disciplines);
    const trimPackageValues = [...(role.trimPackages || [])].sort((left, right) =>
      this.#sortText(left, right),
    );

    return [...disciplineValues, ...trimPackageValues].sort((left, right) => this.#sortText(left, right));
  }

  toggleRole(role: RoleDto) {
    const roleName = role.roleName as RoleName;

    if (role.hidden) {
      this.#unhideRole(roleName);
    } else {
      this.#hideRole(roleName);
    }
  }

  unhideAllRoles(event: Event) {
    event.stopPropagation();
    this.#hideUserRoleApiService.unhideAllRoles().subscribe(() => this.#updateRolesAndAccess());
  }

  #hideRole(roleName: RoleName) {
    this.#hideUserRoleApiService
      .hideUserRole({ roleName }, { projectId: Number(this.#projectId()) })
      .subscribe(() => this.#updateRolesAndAccess());
  }

  #unhideRole(roleName: RoleName) {
    this.#hideUserRoleApiService
      .unhideUserRole({ roleName }, { projectId: Number(this.#projectId()) })
      .subscribe(() => this.#updateRolesAndAccess());
  }

  #normalizeAndSort(values?: string | string[]): string[] {
    const normalizedValues = Array.isArray(values) ? values : (values || '').split(',');

    return normalizedValues
      .map(value => value.trim())
      .filter(value => !!value)
      .sort((left, right) => this.#sortText(left, right));
  }

  #sortText(left: string | undefined, right: string | undefined) {
    return (left || '').localeCompare(right || '', undefined, { sensitivity: 'base' });
  }

  #updateRolesAndAccess() {
    this.userRolesResource.reload();
    this.#viewAccessStore.loadByProjectId(this.#projectId());
    this.#navigationService.navigateToDashboard(this.#projectId());
  }
}
